import {User} from "lucide-react";
import * as m from "@/paraglide/messages";
import {cn} from "@/lib/utils";
import {useIsMobile} from "@/hooks/use-mobile";
import {Dialog, DialogContent, DialogTitle, DialogDescription} from "@/components/ui/dialog";
import {Sheet, SheetContent, SheetTitle, SheetDescription} from "@/components/ui/sheet";
import {ScrollArea} from "@/components/ui/scroll-area";
import {Badge} from "@/components/ui/badge";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar";
import {SocialPostCard} from "@/components/widgets/social-post-card";
import {type SentimentInfluencerWithPosts} from "@/lib/influencers-api";
import {sentimentPostToPostWidget} from "@/lib/post-adapters";
import {getSentimentBadgeClasses, getSentimentLabel} from "@/lib/sentiment-utils";
import {getPlatformInfo, getProfileImageUrl} from "@/lib/social-platform-utils";

interface InfluencerPostsModalProps {
  influencer: SentimentInfluencerWithPosts | null;
  isOpen: boolean;
  onClose: () => void;
}

function ModalHeader({
  influencer,
  isMobile = false,
}: {
  influencer: SentimentInfluencerWithPosts;
  isMobile?: boolean;
}) {
  const profileImageUrl = getProfileImageUrl(influencer.profile_picture);
  const platformInfo = getPlatformInfo(influencer.primary_platform);

  return (
    <div
      className={cn(
        "border-border/50 bg-muted/30 shrink-0 border-b px-4 py-4 sm:px-6 sm:py-5",
        isMobile && "pt-14" // Extra top padding for close button on mobile sheet
      )}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        {/* Avatar with platform badge */}
        <div className="relative shrink-0">
          <Avatar className="ring-border/50 h-12 w-12 ring-1 sm:h-14 sm:w-14">
            <AvatarImage
              src={profileImageUrl || undefined}
              alt={influencer.author || "Influencer"}
            />
            <AvatarFallback className="bg-muted/50">
              <User className="text-muted-foreground h-6 w-6" />
            </AvatarFallback>
          </Avatar>
          {platformInfo && (
            <div
              className={cn(
                "absolute -right-1 -bottom-1 flex h-6 w-6 items-center justify-center rounded-full text-xs",
                platformInfo.bgClass,
                "ring-background ring-2"
              )}
            >
              {platformInfo.emoji}
            </div>
          )}
        </div>

        {/* Name, handle, and sentiment badge */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="truncate text-lg font-semibold tracking-tight sm:text-xl">
              {influencer.author || "Unknown"}
            </h2>
            <Badge
              variant="outline"
              className={cn("shrink-0 font-medium", getSentimentBadgeClasses(influencer.sentiment))}
            >
              {getSentimentLabel(influencer.sentiment)}
            </Badge>
          </div>
          {influencer.handle && (
            <p className="text-muted-foreground mt-1 truncate text-sm">
              @{influencer.handle.replace(/^@/, "")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalContent({influencer}: {influencer: SentimentInfluencerWithPosts}) {
  const posts = influencer.posts || [];

  return (
    <ScrollArea className="flex min-h-0 flex-1 flex-col">
      <div className="space-y-3 p-4 sm:p-6">
        {posts.length === 0 ? (
          <div className="text-muted-foreground py-12 text-center text-sm">No posts available</div>
        ) : (
          posts.map((post) => (
            <SocialPostCard
              key={post.id}
              post={sentimentPostToPostWidget(post)}
              hideInfluenceButton
              detailedEngagement={{
                likes: post.likes,
                shares: post.shares,
                comments: post.comments,
              }}
            />
          ))
        )}
      </div>
    </ScrollArea>
  );
}

export function InfluencerPostsModal({influencer, isOpen, onClose}: InfluencerPostsModalProps) {
  const isMobile = useIsMobile();

  if (!influencer) return null;

  const title = m.influencers_posts_title({name: influencer.author || "Influencer"});

  if (isMobile) {
    return (
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="bottom" className="flex h-[85vh] flex-col p-0">
          <SheetTitle className="sr-only">{title}</SheetTitle>
          <SheetDescription className="sr-only">Posts by {influencer.author}</SheetDescription>
          <ModalHeader influencer={influencer} isMobile />
          <ModalContent influencer={influencer} />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="flex h-[85vh] max-h-[85vh] flex-col overflow-hidden p-0 sm:max-w-2xl"
        showCloseButton
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <DialogDescription className="sr-only">Posts by {influencer.author}</DialogDescription>
        <ModalHeader influencer={influencer} />
        <ModalContent influencer={influencer} />
      </DialogContent>
    </Dialog>
  );
}
